import React, { useState, useEffect, useRef } from 'react';
import styles from './GetInTouch.module.css';
import AddressFooter from 'Utils/AddressFooter';

const GetInTouch = () => {
  const getInTouchImage = '/Assets/Images/GetInTouch/GetInTouch.svg';
  const API_BASE_URL = process.env.REACT_APP_CMS_BASE_URL;
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [recaptchaId, setRecaptchaId] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    if (window.grecaptcha?.render) {
      setRecaptchaLoaded(true);
      setTimeout(renderRecaptcha, 100);
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    
    window.onRecaptchaLoad = () => {
      setRecaptchaLoaded(true);
      renderRecaptcha();
    };
    
    script.onerror = () => setRecaptchaError(true);
    document.head.appendChild(script);

    return () => {
      if (recaptchaId !== null && window.grecaptcha?.reset) {
        try { window.grecaptcha.reset(recaptchaId); } catch (e) {}
      }
    };
  }, []);

  const renderRecaptcha = () => {
    if (window.grecaptcha?.render && recaptchaRef.current && recaptchaId === null) {
      try {
        const id = window.grecaptcha.render(recaptchaRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          size: 'normal',
          callback: (response) => setRecaptchaValue(response),
          'expired-callback': () => setRecaptchaValue(null),
          'error-callback': () => setRecaptchaError(true),
        });
        setRecaptchaId(id);
      } catch (error) {
        setRecaptchaError(true);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const isFieldActive = (fieldName) => focusedField === fieldName || formData[fieldName] !== '';

  const isFormValid = () => {
    const { name, email, phone } = formData;
    return name.trim() && email.trim() && phone.trim() && recaptchaValue && (recaptchaLoaded || recaptchaError);
  };

  const validateForm = () => {
    const { name, email, phone } = formData;
    if (!name.trim()) return 'Name is required';
    if (!email.trim()) return 'Email is required';
    if (!phone.trim()) return 'Phone number is required';
    if (!recaptchaLoaded && !recaptchaError) return 'Please wait for security verification to load';
    if (recaptchaLoaded && !recaptchaValue) return 'Please complete the security verification';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email address';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    await submitForm();
  };

  const submitForm = async () => {
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim(),
        recaptcha_response: recaptchaValue || 'captcha_disabled'
      };

      const response = await fetch(`${API_BASE_URL}/api/lets-talk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFormData({ name: '', email: '', phone: '', message: '' });
        if (window.grecaptcha && recaptchaId !== null) {
          window.grecaptcha.reset(recaptchaId);
          setRecaptchaValue(null);
        }
        setSuccess(true);
        setTimeout(() => setSuccess(false), 5000);
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
      if (window.grecaptcha && recaptchaId !== null) {
        window.grecaptcha.reset(recaptchaId);
        setRecaptchaValue(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderField = (name, type = 'text', placeholder, label, required = false) => (
    <div className={styles.formGroup}>
      {type === 'textarea' ? (
        <textarea
          name={name}
          rows="4"
          value={formData[name]}
          onChange={handleInputChange}
          onFocus={() => setFocusedField(name)}
          onBlur={() => setFocusedField(null)}
          disabled={loading}
          required={required}
          className={styles.formTextarea}
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          onFocus={() => setFocusedField(name)}
          onBlur={() => setFocusedField(null)}
          disabled={loading}
          required={required}
          className={styles.formInput}
          placeholder={placeholder}
        />
      )}
      <label className={`${styles.formLabel} ${isFieldActive(name) ? styles.active : ''}`}>
        {label}{required && <span className={styles.required}>*</span>}
      </label>
    </div>
  );

  return (
    <>
      <div className={styles.getInTouchWrapper}>
        <div className={styles.heroSection} style={{ backgroundImage: `url(${getInTouchImage})` }}>
          <div className={styles.heroOverlay}>
            <h1 className={styles.heroTitle}>Get in touch</h1>
          </div>
        </div>

        <div className={styles.formSection}>
          <div className={styles.formWrapper}>
            <form className={styles.contactForm} onSubmit={handleSubmit}>
              {success && (
                <div className={styles.submitStatus}>
                  <p>✓ Message sent successfully! We'll get back to you soon.</p>
                </div>
              )}
              
              {error && (
                <div className={styles.submitError}>
                  <p>⚠ {error}</p>
                </div>
              )}

              {renderField('name', 'text', 'Name', 'Name', true)}
              {renderField('email', 'email', 'Email', 'Email', true)}
              {renderField('phone', 'tel', 'Phone Number', 'Phone Number', true)}
              {renderField('message', 'textarea', 'Message', 'Message', false)}
              
              <div className={styles.recaptchaContainer}>
                <div ref={recaptchaRef}></div>
                {!recaptchaLoaded && !recaptchaError && (
                  <p className={styles.captchaLoading}>Loading security verification...</p>
                )}
              </div>
              
              <button 
                type="submit" 
                className={styles.submitButton}
                disabled={loading || !isFormValid()}
              >
                {loading ? 'Sending...' : 'Send'}
              </button>
            </form>
          </div>
        </div>
      </div>
      <AddressFooter />
    </>
  );
};

export default GetInTouch;